import React, { useEffect, useRef, useState } from "react";
import { ChevronRight, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
    MobileHeroBanner,
    MobileShowcaseCard,
    mobileHeroBanners as defaultBanners,
    mobileShowcaseCards as defaultCards,
} from "./MobileHomeHeroShowcaseData";
import "./MobileHomeHeroShowcase.css";

const AUTO_SCROLL_DELAY = 4500;
const DUMMY_FALLBACK_IMAGE = "/assets/images/logos/salarlogo.png";

type MobileHomeHeroShowcaseProps = {
    cards?: MobileShowcaseCard[];
    banners?: MobileHeroBanner[];
};

const MobileHomeHeroShowcase: React.FC<MobileHomeHeroShowcaseProps> = ({
    cards = defaultCards,
    banners = defaultBanners,
}) => {
    const navigate = useNavigate();
    const bannerScrollRef = useRef<HTMLDivElement>(null);
    const [activeBanner, setActiveBanner] = useState(0);
    const [isBannerPaused, setIsBannerPaused] = useState(false);

    const scrollToBanner = (
        index: number,
        behavior: ScrollBehavior = "smooth"
    ) => {
        const container = bannerScrollRef.current;
        if (!container) return;

        const bannerElements =
            container.querySelectorAll<HTMLElement>(".mobile-home-banner-card");
        const target = bannerElements[index];
        if (!target) return;

        container.scrollTo({
            left: target.offsetLeft - container.offsetLeft,
            behavior,
        });

        setActiveBanner(index);
    };

    useEffect(() => {
        if (isBannerPaused || banners.length <= 1) return;

        const timer = window.setInterval(() => {
            setActiveBanner((current) => {
                const next = (current + 1) % banners.length;
                window.requestAnimationFrame(() =>
                    scrollToBanner(next, "smooth")
                );
                return next;
            });
        }, AUTO_SCROLL_DELAY);

        return () => window.clearInterval(timer);
    }, [isBannerPaused, banners.length]);

    const handleBannerScroll = () => {
        const container = bannerScrollRef.current;
        if (!container) return;

        const bannerElements = Array.from(
            container.querySelectorAll<HTMLElement>(".mobile-home-banner-card")
        );
        if (bannerElements.length === 0) return;

        const containerLeft = container.getBoundingClientRect().left;
        let closestIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;

        bannerElements.forEach((banner, index) => {
            const distance = Math.abs(
                banner.getBoundingClientRect().left - containerLeft
            );
            if (distance < closestDistance) {
                closestDistance = distance;
                closestIndex = index;
            }
        });

        setActiveBanner(closestIndex);
    };

    const handleBannerInteractionStart = () => {
        setIsBannerPaused(true);
    };

    const handleBannerInteractionEnd = () => {
        window.setTimeout(() => setIsBannerPaused(false), 1800);
    };

    const handleImageError = (
        e: React.SyntheticEvent<HTMLImageElement>,
        fallback: string
    ) => {
        e.currentTarget.onerror = null;
        e.currentTarget.src = fallback || DUMMY_FALLBACK_IMAGE;
    };

    return (
        <section className="mobile-home-showcase">
            <div className="mobile-home-banner-shell">
                <div
                    ref={bannerScrollRef}
                    className="mobile-home-banner-scroll"
                    onScroll={handleBannerScroll}
                    onTouchStart={handleBannerInteractionStart}
                    onTouchEnd={handleBannerInteractionEnd}
                    onMouseEnter={handleBannerInteractionStart}
                    onMouseLeave={handleBannerInteractionEnd}
                >
                    {banners.map((banner, index) => (
                        <article
                            className={`mobile-home-banner-card ${
                                activeBanner === index ? "active" : ""
                            }`}
                            key={banner.id}
                            onClick={() =>
                                banner.link && navigate(banner.link)
                            }
                            style={{
                                cursor: banner.link ? "pointer" : "default",
                            }}
                        >
                            <img
                                src={banner.image}
                                alt={banner.alt}
                                loading={index === 0 ? "eager" : "lazy"}
                                onError={(e) =>
                                    handleImageError(
                                        e,
                                        DUMMY_FALLBACK_IMAGE
                                    )
                                }
                            />

                            <div className="mobile-home-banner-shade" />
                            <div className="mobile-home-banner-glow" />

                            <div className="mobile-home-banner-copy">
                                <span>{banner.eyebrow}</span>
                                <strong>{banner.label}</strong>
                            </div>
                        </article>
                    ))}
                </div>

                <div
                    className="mobile-home-banner-progress"
                    aria-label="Banner navigation"
                >
                    {banners.map((banner, index) => (
                        <button
                            type="button"
                            key={banner.id}
                            className={activeBanner === index ? "active" : ""}
                            onClick={() => scrollToBanner(index)}
                            aria-label={`Show banner ${index + 1}`}
                            aria-current={
                                activeBanner === index ? "true" : undefined
                            }
                        >
                            <span />
                        </button>
                    ))}
                </div>
            </div>

            <div className="mobile-home-personalized-heading">
                <div>
                    <span>
                        <Sparkles size={14} strokeWidth={1.9} />
                        MADE FOR YOU
                    </span>
                    <h2>More of what you like</h2>
                </div>
                <small>Swipe to explore</small>
            </div>

            <div className="mobile-home-cluster-scroll">
                {cards.map((card) => (
                    <article
                        className={`mobile-home-cluster-card theme-${card.theme}`}
                        key={card.id || card.title}
                    >
                        <div className="mobile-home-cluster-heading">
                            <div>
                                <span>{card.kicker}</span>
                                <h3>{card.title}</h3>
                            </div>

                            <button
                                type="button"
                                aria-label={`See more ${card.title}`}
                                onClick={() =>
                                    card.seeMoreLink &&
                                    navigate(card.seeMoreLink)
                                }
                            >
                                <ChevronRight size={16} strokeWidth={2.1} />
                            </button>
                        </div>

                        <div className="mobile-home-product-grid">
                            {card.items.map((item, itemIdx) => (
                                <button
                                    type="button"
                                    className="mobile-home-product-tile"
                                    key={item.id || item.name || itemIdx}
                                    onClick={() =>
                                        item.link && navigate(item.link)
                                    }
                                >
                                    <span className="mobile-home-product-image">
                                        <img
                                            src={item.image}
                                            alt={item.name}
                                            loading="lazy"
                                            onError={(e) =>
                                                handleImageError(
                                                    e,
                                                    DUMMY_FALLBACK_IMAGE
                                                )
                                            }
                                        />
                                    </span>

                                    <strong>{item.name}</strong>
                                </button>
                            ))}
                        </div>

                        <button
                            type="button"
                            className="mobile-home-cluster-more"
                            onClick={() =>
                                card.seeMoreLink &&
                                navigate(card.seeMoreLink)
                            }
                        >
                            <span>See all</span>
                            <ChevronRight size={15} strokeWidth={2} />
                        </button>
                    </article>
                ))}
            </div>
        </section>
    );
};

export default MobileHomeHeroShowcase;
